import React, {useEffect, useState} from 'react';
import {Navigate, Outlet, Route, Routes, useNavigate} from 'react-router-dom';

import './index.css';
import $config from './config';

import {ConstantsContextProvider} from './context/ConstantsContext';
import {NotificationContextProvider} from './context/NotificationContext';

import ConfirmResetPassword from './components/ConfirmResetPassword/ConfirmResetPassword';
import CustomPaymentPage from './components/PaymentPage/CustomPaymentPage/CustomPaymentPage';
import Header from './components/Header/Header';
import HistoryPage from './components/PaymentPage/HistoryPage/HistoryPage';
import HomePage from './components/HomePage/HomePage';
import LoginPage from './components/LoginPage/LoginPage';
import NavPanel from './components/NavPanel/NavPanel';
import NomenclaturePage from './components/NomenclaturePage/NomenclaturePage';
import PaymentPage from './components/PaymentPage/PaymentPage';
import PaymentTemplate from './components/PaymentPage/PaymentTemplate';
import ProfilePage from './components/ProfilePage/ProfilePage';
import KKMControlContainer from './components/KKMControlContainer/KKMControlContainer';
import RegistrationInfoPage from './components/RegistrationInfoPage/RegistrationInfoPage';
import PaymentToWalletPage from './components/PaymentPage/PaymentToWalletPage/PaymentToWalletPage';
import instance from './instance';
import {IGetBalanceInfo} from './interfaces/fetch/balance';
import OfferPage from './components/OfferPage/OfferPage';
import InvoicesPage from './components/AdminPage/InvoicesPage/InvoicesPage';
import AdminTemplate from './components/AdminPage/AdminTemplate';
import AdminPage from './components/AdminPage/AdminPage';
import NomenclatureReplicationPage from './components/NomenclaturePage/NomenclatureReplicationPage';
import NomenclatureNtinPage from './components/NomenclaturePage/NomenclatureNtinPage';
import TelegramButton from './components/TelegramButton/TelegramButton';

const App = (): React.JSX.Element => {
    const navigate = useNavigate();
    const DEFAULT_ACTIVE_BLOCK = 'home';
    const isAuthFromStorage = sessionStorage.getItem('isAuth');
    const isAdminFromStorage = sessionStorage.getItem('isAdmin');
    const isSuperAdminFromStorage = sessionStorage.getItem('isSuperAdmin');
    const [isActiveNavElem, setActiveNavElem] = useState<string>(DEFAULT_ACTIVE_BLOCK);
    // Устанавливает/добавляет основному контейнеру css-классы. Что-то вроде: bg home, bg search-bill, bg kkm и т.п.
    const [currentPageClass, setCurrentPageClass] = useState<string>('');
    const [userId, setUserId] = useState<number | null>(null);
    const [inn, setInn] = useState<string | null>(null);
    const [activeKKM, setActiveKKM] = useState<number | null>(null);
    const [isAuth, setAuth] = useState<boolean>(isAuthFromStorage ? JSON.parse(isAuthFromStorage) : false);
    const [isAdmin, setAdmin] = useState<boolean>(isAdminFromStorage ? JSON.parse(isAdminFromStorage) : false);
    const [isSuperAdmin, setSuperAdmin] = useState<boolean>(isSuperAdminFromStorage ? JSON.parse(isSuperAdminFromStorage) : false);
    const [isStorageUserInfoEmpty, setStorageUserInfoEmpty] = useState<boolean>(true);
    const [isCookieError, setCookieError] = useState<boolean>(false);
    const [balance, setBalance] = useState<number | null>(null);

    const logout = (isError: boolean = true) => {
        if ($config.debugMode & $config.D_MSG) {
            console.log('LOGOUT');
        }
        navigate('/');
        setAuth(false);
        setAdmin(false);
        setSuperAdmin(false);
        sessionStorage.clear();
        setUserId(null);
        setInn(null);
        setActiveNavElem('home');
        setActiveKKM(null);
        if (isError) {
            setCookieError(true);
        } else {
            setCookieError(false);
        }
    };

    const getBalanceInfo = async (userId: number) => {
        const request: IGetBalanceInfo = {
            method: 'GetBalanceInfo',
            params: {
                user_id: Number(userId),
            },
        };
        try {
            const response = await instance.post('/LK', request);
            setBalance(response.data?.balance_value);
        } catch (error) {
            console.error('Error GetBalanceInfo:', error);
        }
    };

    useEffect(() => {
        if (userId === null) {
            const userInfoFromStorage = () => {
                const userData = sessionStorage.getItem('userInfo');
                if (!userData) {
                    setStorageUserInfoEmpty(true);
                    return null;
                }
                setStorageUserInfoEmpty(false);
                return JSON.parse(userData);
            };
            const authorizationFromStorage = userInfoFromStorage();
            if (authorizationFromStorage) {
                const isAdminFromStorage = sessionStorage.getItem('isAdmin');
                if (isAdminFromStorage !== null) {
                    setAdmin(() => JSON.parse(isAdminFromStorage));
                }
                const isSuperAdminFromStorage = sessionStorage.getItem('isSuperAdmin');
                if (isSuperAdminFromStorage !== null) {
                    setSuperAdmin(() => JSON.parse(isSuperAdminFromStorage));
                }
                const isInnFromStorage = sessionStorage.getItem('inn');
                if (isInnFromStorage !== null) {
                    setInn(() => String(isInnFromStorage));
                }
                setAuth(() => true);
                setUserId(authorizationFromStorage.user_id);
            } else {
                setAuth(() => false);
                setAdmin(() => false);
                setSuperAdmin(() => false);
                setInn(() => null);
                setStorageUserInfoEmpty(() => false);
            }
        }
        if (isAdmin) {
            const uid = Number(sessionStorage.getItem('currentUserId'));
        }

        if (userId && isAuth && isAdmin) {
            void getBalanceInfo(userId);
        }
    }, [userId, isAuth, isAdmin, isSuperAdmin]);

    const updateBalance = (balanceValue: number) => {
        setBalance(balanceValue);
    };

    return (
        <NotificationContextProvider logout={logout}>
            <ConstantsContextProvider isAuth={isAuth} userId={userId}>
                <Routes>
                    {!isAuth && (
                        <Route
                            path="/"
                            element={
                                <LoginPage
                                    setUserId={setUserId}
                                    setAuth={setAuth}
                                    setAdmin={setAdmin}
                                    setSuperAdmin={setSuperAdmin}
                                    setInn={setInn}
                                    isCookieError={isCookieError}
                                    setCookieError={setCookieError}
                                />
                            }
                        />
                    )}
                    <Route path="/reset_user/:token" element={<ConfirmResetPassword />} />
                    <Route path="/ok" element={<div>Ok</div>} />

                    {isAuth ? (
                        <>
                            <Route
                                path="/*"
                                element={
                                    <div className={`layout${currentPageClass ? ' ' + currentPageClass : ''}`}>
                                        <NavPanel
                                            isActiveNavElem={isActiveNavElem}
                                            setActiveNavElem={setActiveNavElem}
                                            isAdmin={isAdmin}
                                            isSuperAdmin={isSuperAdmin}
                                        />
                                        <div className={'main-page'}>
                                            <Header
                                                isAdmin={isAdmin}
                                                isSuperAdmin={isSuperAdmin}
                                                setActiveNavElem={setActiveNavElem}
                                                logout={logout}
                                                inn={inn}
                                                balance={balance}
                                            />
                                            <hr className={'h-split'} />
                                            <div className={'sections'}>
                                                <Routes>
                                                    <Route
                                                        path="home"
                                                        element={
                                                            <HomePage
                                                                setCurrentPageClass={setCurrentPageClass}
                                                                isAdmin={isAdmin}
                                                                userId={userId}
                                                                activeKKM={activeKKM}
                                                                setActiveKKM={setActiveKKM}
                                                                setActiveNavElem={setActiveNavElem}
                                                            />
                                                        }
                                                    />
                                                    <Route
                                                        path="profile"
                                                        element={
                                                            isAuth && !isAdmin ? (
                                                                <ProfilePage setCurrentPageClass={setCurrentPageClass} userId={userId} />
                                                            ) : isAuth && isAdmin && !isSuperAdmin ? (
                                                                <Navigate to="/home" />
                                                            ) : isAuth && isSuperAdmin ? (
                                                                <Navigate to="/admin" />
                                                            ) : (
                                                                <Navigate to="/" />
                                                            )
                                                        }
                                                    />
                                                    <Route
                                                        path="admin"
                                                        element={
                                                            isAuth && isSuperAdmin ? (
                                                                <AdminTemplate setCurrentPageClass={setCurrentPageClass}>
                                                                    <Outlet />
                                                                </AdminTemplate>
                                                            ) : (
                                                                <Navigate to="/" />
                                                            )
                                                        }
                                                    >
                                                        <Route path="" element={<AdminPage userId={userId} />} />
                                                        <Route path="invoices" element={<InvoicesPage />} />
                                                    </Route>
                                                    <Route
                                                        path="kkm-control"
                                                        element={
                                                            isAuth ? (
                                                                <KKMControlContainer
                                                                    setCurrentPageClass={setCurrentPageClass}
                                                                    userId={userId}
                                                                />
                                                            ) : (
                                                                <Navigate to="/" />
                                                            )
                                                        }
                                                    />
                                                    <Route
                                                        path="nomenklatura"
                                                        element={
                                                            isAuth ? (
                                                                <NomenclaturePage
                                                                    setCurrentPageClass={setCurrentPageClass}
                                                                    userId={userId}
                                                                    isAdmin={isAdmin}
                                                                />
                                                            ) : (
                                                                <Navigate to="/" />
                                                            )
                                                        }
                                                    />
                                                    <Route
                                                        path="nomenklatura/replication"
                                                        element={
                                                            isAuth ? (
                                                                <NomenclatureReplicationPage
                                                                    setCurrentPageClass={setCurrentPageClass}
                                                                    userId={userId}
                                                                    isAdmin={isAdmin}
                                                                />
                                                            ) : (
                                                                <Navigate to="/" />
                                                            )
                                                        }
                                                    />
                                                    <Route
                                                        path="nomenklatura/ntin"
                                                        element={
                                                            isAuth ? (
                                                                <NomenclatureNtinPage
                                                                    setCurrentPageClass={setCurrentPageClass}
                                                                    userId={userId}
                                                                    isAdmin={isAdmin}
                                                                />
                                                            ) : (
                                                                <Navigate to="/" />
                                                            )
                                                        }
                                                    />
                                                    <Route
                                                        path="payment"
                                                        element={
                                                            isAuth && isAdmin ? (
                                                                <PaymentTemplate setCurrentPageClass={setCurrentPageClass}>
                                                                    <Outlet />
                                                                </PaymentTemplate>
                                                            ) : (
                                                                <Navigate to="/" />
                                                            )
                                                        }
                                                    >
                                                        <Route path="" element={<PaymentPage />} />
                                                        <Route path="custompayment" element={<CustomPaymentPage userId={userId} />} />
                                                        {/*<Route*/}
                                                        {/*    path="paymenttowallet"*/}
                                                        {/*    element={*/}
                                                        {/*        <PaymentToWalletPage*/}
                                                        {/*            userId={userId}*/}
                                                        {/*            currentBalance={balance}*/}
                                                        {/*            updateBalance={updateBalance}*/}
                                                        {/*        />*/}
                                                        {/*    }*/}
                                                        {/*/>*/}
                                                        <Route path="history" element={<HistoryPage userId={userId} />} />
                                                    </Route>
                                                </Routes>
                                            </div>
                                            <div
                                                style={{
                                                    textAlign: 'center',
                                                    paddingTop: '40px',
                                                    paddingBottom: '20px',
                                                    fontSize: '12px',
                                                    color: '#666',
                                                }}
                                            >
                                                АПК Портал &mdash;{' '}
                                                <a href={'/offer'} target={'_blank'} style={{color: '#666'}}>
                                                    Договор-оферта
                                                </a>
                                            </div>
                                        </div>
                                        {isAdmin ? <TelegramButton href="https://t.me/PORTall_support_bot"></TelegramButton> : <></>}
                                    </div>
                                }
                            />
                            <Route path="/" element={<Navigate to="/home" />} />
                        </>
                    ) : (
                        <Route path="*" element={<Navigate to="/" />} />
                    )}
                    <Route
                        path="merchantinfo"
                        element={<RegistrationInfoPage isAuth={isAuth} isAdmin={isAdmin} isSuperAdmin={isSuperAdmin} />}
                    />
                    <Route path="offer" element={<OfferPage isAuth={isAuth} isAdmin={isAdmin} isSuperAdmin={isSuperAdmin} />} />
                </Routes>
            </ConstantsContextProvider>
        </NotificationContextProvider>
    );
};

export default App;
